import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Send, Plus, ClipboardList, ArrowLeft, Download, Printer,
  ChevronRight, FileText, ShieldAlert, Archive, ArchiveX, Loader2, Filter, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  callQuestionnairePrefill,
  type QuestionnairePrefillField,
  type QuestionnairePrefillValue,
} from '@/lib/functions';
import { mergePrefill } from '@/lib/questionnairePrefill';
import {
  createQuestionnaire, listOrgQuestionnaires,
  saveQuestionnaireData, submitQuestionnaire, watchQuestionnaire,
} from '@/lib/firestore';
import { cn, fmtDate, fmtRelative, statusColor, STATUS_KEY } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { labelClassName } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { Loader } from '@/components/ui/loader';
import { EmptyState } from '@/components/ui/empty-state';
import { SegmentedControl } from '@/components/SegmentedControl';
import { SaveIndicator, type SaveState } from '@/components/SaveIndicator';
import type { QuestionnaireDoc } from '@/types/models';

interface ProgressData {
  filled: number;
  total: number;
  percent: number;
  sections: { key: string; id: string; title: string; filled: number; total: number }[];
}

// ─── List view ─────────────────────────────────────────────────
function QuestionnaireList() {
  const { effectiveOrgId, user, isGlobalAdmin, claims } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [list, setList] = useState<QuestionnaireDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<'all' | 'draft' | 'submitted'>('all');
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    if (!effectiveOrgId) {
      setList([]);
      setLoadError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listOrgQuestionnaires(effectiveOrgId)
      .then(qs => {
        if (!cancelled) {
          setList(qs);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setList([]);
          setLoadError(e.message);
          setLoading(false);
          toast.error(e.message);
        }
      });
    return () => { cancelled = true; };
  }, [effectiveOrgId, reloadKey]);

  const startNew = async () => {
    if (!effectiveOrgId || !user) return;
    const id = await createQuestionnaire(effectiveOrgId, user.uid, isGlobalAdmin);
    nav(`/questionnaire/${id}`);
  };

  const latestQuestionnaire = list[0];
  const archiveQuestionnaires = useMemo(() => list.slice(1), [list]);

  const filteredArchive = useMemo(() => {
    return archiveQuestionnaires.filter(q => filter === 'all' || q.status === filter);
  }, [archiveQuestionnaires, filter]);

  if (loading) return <Loader fullscreen label={t('common.loading')} />;

  if (isGlobalAdmin && !claims.assumedOrgId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-ink-900 dark:text-white">{t('q.title')}</h1>
          <p className="mt-1 text-sm text-ink-500">{t('q.subtitle')}</p>
        </div>
        <Card>
          <EmptyState
            className="border-0 bg-transparent py-10 dark:border-0 dark:bg-transparent"
            icon={<ClipboardList className="h-5 w-5" />}
            title={t('q.no_org.title')}
            description={t('q.no_org.desc')}
            action={<Button onClick={() => nav('/admin/orgs')}>{t('q.no_org.cta')}</Button>}
          />
        </Card>
      </div>
    );
  }

  const stats = {
    total: list.length,
    draft: list.filter(q => q.status === 'draft').length,
    submitted: list.filter(q => q.status === 'submitted').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-ink-900 dark:text-white">{t('q.title')}</h1>
          <p className="mt-1 text-sm text-ink-500">{t('q.subtitle')}</p>
        </div>
        <Button onClick={startNew}><Plus className="h-4 w-4" /> {t('q.new')}</Button>
      </div>

      {list.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label={t('q.stat.total')} value={stats.total} />
          <Stat label={t('q.stat.draft')} value={stats.draft} tone="warning" />
          <Stat label={t('q.stat.submitted')} value={stats.submitted} tone="info" />
        </div>
      )}

      {loadError ? (
        <Card>
          <EmptyState
            className="border-0 bg-transparent py-10 dark:border-0 dark:bg-transparent"
            icon={<ClipboardList className="h-5 w-5" />}
            title={t('q.load_error.title')}
            description={loadError}
            action={<Button onClick={() => setReloadKey(k => k + 1)}>{t('common.retry')}</Button>}
          />
        </Card>
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            className="border-0 bg-transparent py-10 dark:border-0 dark:bg-transparent"
            icon={<ClipboardList className="h-5 w-5" />}
            title={t('q.empty.title')}
            description={t('q.empty.desc')}
            action={<Button onClick={startNew}><Plus className="h-4 w-4" /> {t('q.empty.start')}</Button>}
          />
        </Card>
      ) : (
        <Card className="space-y-6">
          {/* Card Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 dark:border-border/50">
            <div>
              <h2 className="font-display text-lg text-ink-900 dark:text-white">
                {showArchive ? t('q.archive') : t('q.latest')}
              </h2>
              <p className="mt-1 text-xs text-ink-500">
                {showArchive ? t('q.subtitle') : t('q.latest_desc')}
              </p>
            </div>
            {list.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowArchive(!showArchive)}
                className="text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20"
              >
                {showArchive ? (
                  <>
                    <ArchiveX className="mr-1.5 h-4 w-4" />
                    {t('q.hide_archive')}
                  </>
                ) : (
                  <>
                    <Archive className="mr-1.5 h-4 w-4" />
                    {t('q.show_archive')}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Latest Questionnaire */}
          <div>
            <ul className="divide-y divide-slate-200 dark:divide-border/50">
              <QuestionnaireRow q={latestQuestionnaire} onOpen={() => nav(`/questionnaire/${latestQuestionnaire.id}`)} />
            </ul>
          </div>

          {/* Archive Section */}
          {showArchive && (
            <div className="border-t border-slate-100 pt-6 dark:border-border/50">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold text-ink-700 dark:text-ink-200">
                  {t('q.older_questionnaires')}
                </h3>
                <SegmentedControl
                  options={(['all', 'draft', 'submitted'] as const).map(f => ({
                    id: f,
                    label: f === 'all' ? t('q.filter.all') : f === 'draft' ? t('q.filter.draft') : t('q.filter.submitted'),
                    icon: f === 'all' ? Filter : undefined,
                  }))}
                  value={filter}
                  onChange={setFilter}
                />
              </div>

              {filteredArchive.length === 0 ? (
                <p className="text-center text-sm text-ink-500 py-6">{t('empty.no_results')}</p>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-border/50">
                  {filteredArchive.map(q => (
                    <QuestionnaireRow key={q.id} q={q} onOpen={() => nav(`/questionnaire/${q.id}`)} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function QuestionnaireRow({ q, onOpen }: { q: QuestionnaireDoc; onOpen: () => void }) {
  const { t, locale } = useT();
  const fieldCount = useMemo(() => {
    const data = q.data as Record<string, unknown>;
    if (!data) return null;
    return Object.keys(data).filter(k => k !== '_meta').length;
  }, [q.data]);

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-center gap-4 py-3 text-left transition first:pt-0 last:pb-0 hover:text-brand-600 dark:hover:text-primary"
      >
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
          <ClipboardList className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={statusColor(q.status)}>{t(STATUS_KEY[q.status])}</span>
            {q.filledOnBehalfOfClient && (
              <Badge variant="warning" className="inline-flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" /> {t('q.by_admin')}
              </Badge>
            )}
          </div>
          <div className="mt-1 truncate font-medium text-ink-900 group-hover:text-brand-600 dark:text-white dark:group-hover:text-primary">
            {t('q.title')}
          </div>
          <div className="mt-0.5 text-xs text-ink-500">
            {t('common.updated')} {fmtRelative(q.updatedAt, t, locale)}
            {q.filledByName && <> · {t('q.col.user')} {q.filledByName}</>}
            {fieldCount != null && <> · {t('q.fields_filled', { n: fieldCount })}</>}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-400 transition group-hover:translate-x-0.5 group-hover:text-brand-500 dark:group-hover:text-primary" />
      </button>
    </li>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'info' | 'warning' }) {
  const cls = tone === 'info'
    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
    : tone === 'warning'
      ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-slate-50 text-slate-700 dark:bg-ink-700 dark:text-ink-200';
  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className={labelClassName()}>{label}</div>
        <div className={`rounded-md px-2 py-0.5 text-xs font-semibold ${cls}`}>{value}</div>
      </div>
    </Card>
  );
}

// ─── Detail view ───────────────────────────────────────────────
function QuestionnaireDetail({ id }: { id: string }) {
  const { isGlobalAdmin } = useAuth();
  const { t, locale } = useT();
  const nav = useNavigate();
  const [doc, setDoc]               = useState<QuestionnaireDoc | null>(null);
  const [loading, setLoading]       = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [progress, setProgress]     = useState<ProgressData | null>(null);
  const [saveState, setSaveState]   = useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastSentJson = useRef('');
  const docDataRef   = useRef<Record<string, unknown> | null>(null);
  // Pré-remplissage depuis le dernier audit : faits matériels mesurés,
  // fusionnés SOUS les réponses saisies (l'humain gagne toujours).
  const [prefill, setPrefill] = useState<Record<string, QuestionnairePrefillValue>>({});
  const [prefillFields, setPrefillFields] = useState<QuestionnairePrefillField[]>([]);
  const [prefillSystem, setPrefillSystem] = useState<string | null>(null);
  const prefillRef = useRef<Record<string, QuestionnairePrefillValue>>({});

  // Réponses envoyées à l'iframe : le pré-remplissage sert de socle, toute
  // réponse déjà saisie l'écrase.
  const mergedHydration = useCallback(
    (data: Record<string, unknown> | null | undefined) =>
      mergePrefill(prefillRef.current, data),
    [],
  );

  const requestIframeReady = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'questionnaire:requestReady' },
      window.location.origin,
    );
  }, []);

  useEffect(() => {
    let vivant = true;
    callQuestionnairePrefill({ lang: locale })
      .then(({ data }) => {
        if (!vivant) return;
        prefillRef.current = data.prefill ?? {};
        setPrefill(data.prefill ?? {});
        setPrefillFields(data.fields ?? []);
        setPrefillSystem(data.systemName ?? null);
      })
      .catch(() => { /* pré-remplissage best-effort : jamais bloquant */ });
    return () => { vivant = false; };
  }, [locale]);

  useEffect(() => watchQuestionnaire(
    id,
    q => { setDoc(q); setLoading(false); docDataRef.current = (q?.data as Record<string, unknown>) ?? null; },
    err => { setLoading(false); toast.error(err.message); },
  ), [id]);

  // Bridge messages
  useEffect(() => {
    const handler = async (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const m = ev.data;
      if (!m || typeof m !== 'object') return;

      if (m.type === 'questionnaire:ready') {
        setIframeLoaded(true);
        const cw = iframeRef.current?.contentWindow;
        if (!cw) return;
        // Push current locale to the embedded form
        cw.postMessage({ type: 'questionnaire:setLang', lang: locale }, window.location.origin);
        // Hydrate : pré-remplissage sous les réponses sauvées.
        cw.postMessage(
          { type: 'questionnaire:load', data: mergedHydration(docDataRef.current) },
          window.location.origin,
        );
      }

      if (m.type === 'questionnaire:progress') {
        setProgress(m.progress as ProgressData);
      }

      if (m.type === 'questionnaire:change') {
        const json = JSON.stringify(m.data);
        if (json === lastSentJson.current) return;
        lastSentJson.current = json;
        // Only flip to "saving" if it actually takes a while —
        // avoids flicker on every keystroke when saves complete in <500 ms.
        const showSavingTimer = window.setTimeout(() => setSaveState('saving'), 500);
        try {
          await saveQuestionnaireData(id, m.data);
          window.clearTimeout(showSavingTimer);
          setSaveState('saved');
          setLastSavedAt(new Date());
        } catch (e) {
          window.clearTimeout(showSavingTimer);
          setSaveState('error');
          toast.error((e as Error).message);
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [id]);

  // Push locale changes to the iframe whenever the user toggles FR/EN
  useEffect(() => {
    if (!iframeLoaded) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'questionnaire:setLang', lang: locale },
      window.location.origin,
    );
  }, [locale, iframeLoaded]);

  // Re-handshake if the iframe announced ready before our listener was attached (StrictMode / fast cache).
  useEffect(() => {
    if (iframeLoaded) return;
    requestIframeReady();
    const retry = window.setInterval(requestIframeReady, 800);
    const stop = window.setTimeout(() => window.clearInterval(retry), 8000);
    return () => { window.clearInterval(retry); window.clearTimeout(stop); };
  }, [iframeLoaded, requestIframeReady]);

  // Re-hydrate when doc data changes from another tab/admin
  useEffect(() => {
    if (!iframeLoaded) return;
    const merged = mergedHydration(doc?.data as Record<string, unknown> | null);
    const json = JSON.stringify(merged);
    if (json === lastSentJson.current) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'questionnaire:load', data: merged },
      window.location.origin,
    );
  }, [doc?.data, iframeLoaded, prefill, mergedHydration]);

  const scrollTo = (sectionId: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'questionnaire:scrollTo', id: sectionId },
      window.location.origin,
    );
  };

  const exportJson = () => {
    const data = (doc?.data ?? {}) as Record<string, unknown>;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    triggerDownload(blob, `questionnaire-${id}.json`);
    toast.success('Export JSON téléchargé.');
  };

  const print = () => {
    iframeRef.current?.contentWindow?.print();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await submitQuestionnaire(id);
      setSubmitOpen(false);
      toast.success(t('q.submitted'));
    } catch (e) { toast.error((e as Error).message); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Loader fullscreen />;
  if (!doc) return <Card>{t('audit.not_found')}</Card>;

  const isSubmitted = doc.status === 'submitted';

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-ink-500" aria-label="Breadcrumb">
        <Link to="/dashboard" className="hover:text-brand-600">{t('q.bc.dashboard')}</Link>
        <ChevronRight className="h-3 w-3 text-ink-400" />
        <Link to="/questionnaire" className="hover:text-brand-600">{t('q.bc.questionnaires')}</Link>
        <ChevronRight className="h-3 w-3 text-ink-400" />
        <span className="font-mono text-ink-700 dark:text-ink-200">{id.slice(0, 8)}…</span>
      </nav>

      {/* Header card */}
      <Card className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <button
            onClick={() => nav('/questionnaire')}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-ink-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-200 dark:hover:bg-brand-900/30 dark:hover:text-brand-300"
            aria-label={t('q.back_label')}
            title={t('common.back')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl sm:text-2xl text-ink-900 dark:text-white">{t('q.title')}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-ink-500">
              <span className={statusColor(doc.status)}>{t(STATUS_KEY[doc.status])}</span>
              {doc.filledOnBehalfOfClient && (
                <Badge variant="warning" className="inline-flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3" /> {t('q.by_admin')}
                </Badge>
              )}
              <span>{t('common.created')} {fmtDate(doc.createdAt, locale)}</span>
              {doc.submittedAt && <span>· {t('status.submitted')} {fmtDate(doc.submittedAt, locale)}</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <SaveIndicator state={saveState} lastSavedAt={lastSavedAt} />
          <div className="hidden h-5 w-px bg-slate-200 sm:block dark:bg-ink-700" />
          <Button variant="secondary" onClick={exportJson}><Download className="h-3.5 w-3.5" /> {t('q.actions.json')}</Button>
          <Button variant="secondary" onClick={print}><Printer className="h-3.5 w-3.5" /> {t('q.actions.print')}</Button>
          {!isSubmitted && (
            <Button onClick={() => setSubmitOpen(true)}>
              <Send className="h-4 w-4" /> {t('q.actions.submit')}
            </Button>
          )}
        </div>
      </Card>

      {prefillFields.length > 0 && (
        <Card className="!p-4 border-brand-200 bg-brand-50/50 dark:border-brand-900/40 dark:bg-brand-900/10">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-300" />
            <div className="min-w-0 text-sm">
              <div className="font-medium text-ink-800 dark:text-ink-100">
                {t('q.prefill.title', { n: prefillFields.length, system: prefillSystem ?? t('q.prefill.your_machine') })}
              </div>
              <p className="mt-0.5 text-xs text-ink-600 dark:text-ink-300">{t('q.prefill.hint')}</p>
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {prefillFields.map(f => (
                  <li key={f.name}
                      className="rounded bg-white px-2 py-0.5 text-xs text-ink-700 shadow-sm dark:bg-ink-800 dark:text-ink-200">
                    <span className="text-ink-500">{f.label} :</span>{' '}
                    <span className="font-medium">{f.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {progress && (
        <Card className="!p-4">
          <div className="flex items-center justify-between text-xs">
            <div className="font-medium text-ink-700 dark:text-ink-200">{t('q.progress')}</div>
            <div className="font-mono text-ink-600 dark:text-ink-300">{t('q.progress.fields', { filled: progress.filled, total: progress.total, pct: progress.percent })}</div>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-ink-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400 transition-all duration-500"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </Card>
      )}

      {/* Body — same viewport height for section nav + questionnaire iframe */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
        {/* Section nav */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 flex h-[calc(100vh-120px)] flex-col overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-soft dark:border-ink-700 dark:bg-ink-800">
            <div className={cn(labelClassName(), 'mb-2 px-2')}>{t('q.sections')}</div>
            {!progress && <div className="px-2 text-xs text-ink-500">{t('common.loading')}</div>}
            {progress?.sections.map(s => {
              const ratio = s.total ? s.filled / s.total : 0;
              const tone = ratio === 0 ? 'bg-slate-300 dark:bg-ink-600'
                          : ratio < 0.5 ? 'bg-amber-400'
                          : ratio < 1   ? 'bg-brand-400'
                          : 'bg-emerald-500';
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className="group flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-ink-700"
                  title={s.title}
                >
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ink-700 group-hover:text-brand-600 dark:text-ink-100 dark:group-hover:text-brand-300">
                      {s.title}
                    </span>
                    <span className="text-[0.65rem] text-ink-500">{s.filled}/{s.total}</span>
                  </span>
                </button>
              );
            })}
            {isGlobalAdmin && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-[0.7rem] text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300">
                <ShieldAlert className="mr-1 inline h-3 w-3" /> {t('q.admin_warning')}
              </div>
            )}
          </div>
        </aside>

        {/* iframe — same height as section nav */}
        <div className="relative h-[70vh] min-h-[500px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft dark:border-border/50 dark:bg-card/50 dark:backdrop-blur-sm lg:h-[calc(100vh-120px)] lg:min-h-0">
          {!iframeLoaded && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white dark:bg-ink-800">
              <div className="flex items-center gap-2 text-sm text-ink-500">
                <Loader2 className="h-4 w-4 animate-spin" /> {t('q.loading')}
              </div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src="/embed/questionnaire/index.html?embed=1"
            title="Questionnaire AI Readiness"
            className="h-full w-full border-0"
            onLoad={requestIframeReady}
          />
        </div>
      </div>

      <Dialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        title={t('q.submit_modal.title')}
        description={t('q.submit_modal.desc')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSubmitOpen(false)} disabled={submitting}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {t('q.submit_modal.cta')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {progress && (
            <div className={`rounded-lg p-3 text-sm ${progress.percent < 80 ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'}`}>
              <div className="font-medium">{t('q.progress')} : {progress.percent}% ({progress.filled}/{progress.total})</div>
              {progress.percent < 80 && (
                <div className="mt-1 text-xs">{t('q.submit_warn')}</div>
              )}
            </div>
          )}
          <div className="text-sm text-ink-700 dark:text-ink-200">
            <FileText className="mr-1.5 inline h-4 w-4 text-brand-500" />
            {t('q.copy_sent')}
          </div>
          <Link to={`/questionnaire/${id}`} className="block rounded-md bg-slate-50 px-3 py-2 text-xs text-ink-500 dark:bg-ink-900/50">
            {t('q.identifier')} : <code>{id}</code>
          </Link>
        </div>
      </Dialog>
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Page entry ────────────────────────────────────────────────
export default function Questionnaire() {
  const { questionnaireId } = useParams();
  if (!questionnaireId) return <QuestionnaireList />;
  return <QuestionnaireDetail id={questionnaireId} />;
}
